import { getProjectModelRevision } from '../project/project_revision_service.js';
import { activeTool } from '../interaction/tool_service.js?v=20260904f';
import * as AppController from '../app/app_controller.js?v=20260904f';
import * as SelectionController from './selection_controller.js?v=20260904f';
import {
  allMultiSelectionFields,
  emptyMultiSelection,
  syncLegacySelectionFromMultiSelection
} from './selection_controller.js?v=20260904f';
import * as EditCommandController from '../interaction/edit_command_controller.js?v=20260903c';
import * as SelectionService from './selection_service.js?v=20260904f';
import {
  getPlanSvg,
  screenToWorld,
  setStatus
} from '../render/plan_renderer.js?v=20260904f';
import * as SystemRegistry from '../registry/system_registry.js?v=20260904f';
import { uid } from '../utils/common_constants.js?v=20260706d';
import {
  ensureObstacleState,
  normalizeObstacle,
  selectObstacle,
  selectedObstacles
} from '../obstacles/obstacles.js?v=20260903c';
import { S } from '../app/app_state.js?v=20260817c';
import {
  cloneDeep,
  recordUndo
} from '../services/history_service.js?v=20260903c';

const CLIPBOARD_PASTE_OFFSET_MM = 50;
const CLIPBOARD_PASTE_PAN_TOLERANCE_PX = 4;
const CLIPBOARD_PASTE_PENDING_KEY = 'clipboardPastePending';

function handleClipboardKey(e) {
    if (!(e.ctrlKey || e.metaKey) || e.altKey) return false;
    const key = String(e.key || '').toLowerCase();
    const isCopy = e.code === 'KeyC' || key === 'c';
    const isPaste = e.code === 'KeyV' || key === 'v';
    if (!isCopy && !isPaste) return false;
    if (EditCommandController.isTypingTarget(e.target)) return false;

    // Trusted shortcuts produce native ClipboardEvents with permission-free data.
    // Synthetic commands and application menus keep using the internal clipboard.
    if (e.isTrusted) return true;

    if (isCopy) {
      e.preventDefault();
      copyCurrentSelectionToClipboard();
      return true;
    }

    if (isPaste) {
      e.preventDefault();
      pasteFromAppClipboard();
      return true;
    }

    return false;
  }

function copyCurrentSelectionToClipboard() {
    SelectionService.syncFromLegacy();
    const multiSelection = SelectionController.resolvedMultiSelection();
    const mixedPayload = buildCurrentMixedClipboardPayload(multiSelection);
    const mixedTotal = clipboardPayloadObjectCount(mixedPayload);
    if (mixedTotal > 0) {
      setAppClipboard(mixedPayload);
      setStatus(`${mixedTotal} Objekt${mixedTotal === 1 ? '' : 'e'} kopiert`);
      return true;
    }

    const selectionResult = SystemRegistry.buildActiveSelectionClipboardPayload();
    if (selectionResult?.payload) {
      setAppClipboard(selectionResult.payload);
      setStatus(selectionResult.statusMessage || 'Kopiert');
      return true;
    }

    setStatus('Keine Auswahl zum Kopieren');
    return false;
  }

function buildCurrentMixedClipboardPayload(multiSelection = SelectionController.resolvedMultiSelection()) {
    const entries = SystemRegistry.buildClipboardEntries({ multiSelection });
    const hasDrawingSelection = entries.length > 0;
    const obstacles = !hasDrawingSelection ? selectedObstacles() : [];
    return {
      kind: 'mixedObjects',
      systemEntries: entries.map(entry => ({
        systemId: entry.systemId,
        key: entry.key,
        items: entry.items
      })),
      obstacles: obstacles.map(obstacle => cloneDeep(obstacle))
    };
  }

function clipboardUniqueIds(values = []) {
    return [...new Set((values || []).filter(Boolean))];
  }

function clipboardPayloadObjectCount(payload = {}) {
    return clipboardPasteEntries(payload).reduce((sum, e) => sum + e.items.length, 0)
      + (payload.obstacles?.length || 0);
  }

function setAppClipboard(payload) {
    S.clipboard = {
      app: 'kern-studer-zeichnungsprogramm',
      version: 1,
      pasteCount: 0,
      payload
    };
  }

function pasteFromAppClipboard() {
    const clipboard = S.clipboard;
    if (!clipboard?.payload) {
      setStatus('Keine kopierte Auswahl vorhanden');
      return false;
    }

    if (!validOwnerPaste(clipboard.payload)) return false;
    if (startClipboardPastePending(clipboard.payload)) return true;

    clipboard.pasteCount = (clipboard.pasteCount || 0) + 1;
    const offset = CLIPBOARD_PASTE_OFFSET_MM * clipboard.pasteCount;

    if (SystemRegistry.pasteLegacyClipboardPayload(clipboard.payload, offset)) return true;

    if (clipboard.payload.kind === 'mixedObjects') {
      return pasteMixedObjects(clipboard.payload, offset);
    }

    setStatus('Zwischenablage passt nicht zu dieser Zeichnung');
    return false;
  }

function copyToClipboardEvent(event) {
    if (!copyCurrentSelectionToClipboard()) return false;
    event.preventDefault();
    try { event.clipboardData?.setData('text/plain', JSON.stringify(S.clipboard)); }
    catch { /* The internal copy remains available if the browser rejects export. */ }
    return true;
  }

function pasteFromClipboardEvent(event) {
    event.preventDefault();
    // Preserve application-copy priority and repeated paste offsets.
    if (S.clipboard?.payload) return pasteFromAppClipboard();
    try { return pasteFromSystemClipboard(event.clipboardData?.getData('text/plain') || ''); }
    catch { return pasteFromAppClipboard(); }
  }

  async function pasteFromSystemClipboard(clipboardText) {
    try {
      const text = typeof clipboardText === 'string' ? clipboardText : await navigator.clipboard?.readText?.();
      const parsed = text ? JSON.parse(text) : null;
      if (parsed?.app !== 'kern-studer-zeichnungsprogramm' || !parsed.payload) {
        setStatus('Keine kopierte Auswahl vorhanden');
        return false;
      }
      S.clipboard = { ...parsed, pasteCount: 0 };
      return pasteFromAppClipboard();
    } catch (_err) {
      setStatus('Keine kopierte Auswahl vorhanden');
      return false;
    }
  }

// Owner commands provide all-or-none validation and their own scoped history.
// Mixing that transaction with legacy host writes would allow partial pastes.
function ownerPaste(payload) {
  return clipboardPasteEntries(payload).some(entry=>SystemRegistry.getSystem(entry.systemId)?.clipboardCommitMode==='owner');
}
function validOwnerPaste(payload) {
  if(!ownerPaste(payload))return true;
  const entries=clipboardPasteEntries(payload);
  if(entries.length===1 && (!payload.systemEntries || payload.systemEntries.length===1) && !payload.obstacles?.length)return true;
  setStatus('Diese Kopie bitte getrennt nach System einfügen.');return false;
}

function pasteMixedObjects(payload, offset) {
    const expectedTotal = clipboardPayloadObjectCount(payload);
    if (!expectedTotal) return false;
    if(!validOwnerPaste(payload))return false;
    if(!ownerPaste(payload))recordUndo(expectedTotal === 1 ? 'Objekt einfuegen' : 'Objekte einfuegen');

    const fields = allMultiSelectionFields();
    const ids = emptyMultiSelection();

    for (const entry of clipboardPasteEntries(payload)) {
      const result = SystemRegistry.pasteClipboardPayload(entry.systemId, {
        kind: entry.key,
        [entry.key]: entry.items,
        items: entry.items,
        offset
      });
      if (!result) continue;
      for (const f of fields) {
        if (Array.isArray(result[f])) ids[f].push(...result[f]);
      }
    }

    if (payload.obstacles?.length) {
      ids.obstacleIds.push(...pasteObstaclesFromClipboard(payload.obstacles, offset));
    }

    const total = fields.reduce((sum, f) => sum + (ids[f]?.length || 0), 0);
    if (!total) return false;

    selectPastedClipboardObjects(ids);
    AppController.refresh(false);
    setStatus(`${total} Objekt${total === 1 ? '' : 'e'} eingefuegt`);
    return true;
  }

function clipboardPasteEntries(payload = {}) {
    if (!payload || typeof payload !== 'object') return [];
    const legacy = SystemRegistry.clipboardPasteEntriesLegacy(payload);
    if (legacy.length) return legacy;
    return SystemRegistry.clipboardPasteEntries(payload);
  }

function hasClipboardPastePreviewSupport(payload = {}) {
    const entries = clipboardPasteEntries(payload);
    if (!entries.length) return false;
    return entries.every(entry => Boolean(SystemRegistry.createClipboardPreview(entry.systemId, {
      items: entry.items,
      dryRun: true
    })));
  }

function startClipboardPastePending(payload = {}) {
    if (currentClipboardPastePending()) return true;
    if (!hasClipboardPastePreviewSupport(payload)) return false;
    if (!S.ui || typeof S.ui !== 'object') S.ui = {};
    S.ui[CLIPBOARD_PASTE_PENDING_KEY] = {
      active: true,
      ...(ownerPaste(payload) ? {ownerContext:{project:S.project,sheetId:S.drawingSheets?.activeSheetId,revision:getProjectModelRevision(),view:S.project?.currentView,
        tool:JSON.stringify(activeTool()),systemId:SystemRegistry.state.activeSystemId}} : {}),
      payload: cloneDeep(payload),
      entries: clipboardPasteEntries(payload).map(entry => ({
        systemId: entry.systemId,
        key: entry.key,
        count: entry.items.length
      })),
      world: null,
      previews: []
    };
    setStatus('Kopie am Mauszeiger platzieren');
    AppController.requestPlanRender('clipboard-paste-pending');
    return true;
  }

function currentClipboardPastePending() {
    const pending = S.ui?.[CLIPBOARD_PASTE_PENDING_KEY];
    const c=pending?.ownerContext;
    if(c && (c.project!==S.project || c.sheetId!==S.drawingSheets?.activeSheetId || c.revision!==getProjectModelRevision() || c.view!==S.project?.currentView
      || c.tool!==JSON.stringify(activeTool()) || c.systemId!==SystemRegistry.state.activeSystemId)) {
      S.ui[CLIPBOARD_PASTE_PENDING_KEY]=null;return null;
    }
    return pending?.active && pending.payload ? pending : null;
  }

function clearClipboardPastePending(options = {}) {
    if (!S.ui?.[CLIPBOARD_PASTE_PENDING_KEY]) return false;
    S.ui[CLIPBOARD_PASTE_PENDING_KEY] = null;
    if (options.status !== false) setStatus('Bereit');
    AppController.requestPlanRender('clipboard-paste-clear');
    return true;
  }

function clipboardPointerWorld(event, raw = false) {
    const svg = getPlanSvg();
    const rect = svg?.getBoundingClientRect?.();
    if (!event || !rect) return null;
    const world = screenToWorld(event.clientX - rect.left, event.clientY - rect.top);
    if (raw || !S.project?.snap) return world;
    const grid = Number(S.project.gridSize);
    const gridSize = Number.isFinite(grid) && grid > 0 ? grid : 10;
    return {
      x: Math.round(world.x / gridSize) * gridSize,
      z: Math.round(world.z / gridSize) * gridSize
    };
  }

function updateClipboardPastePreview(event) {
    const pending = currentClipboardPastePending();
    if (!pending) return false;
    if (updateClipboardPastePanGesture(pending, event)) return true;
    const world = clipboardPointerWorld(event);
    if (!world) return false;
    const rawWorld = clipboardPointerWorld(event, true);
    const previews = [];
    for (const entry of clipboardPasteEntries(pending.payload)) {
      const preview = SystemRegistry.createClipboardPreview(entry.systemId, {
        items: entry.items,
        world,
        rawWorld,
        target: world
      });
      if (!preview) continue;
      previews.push(preview);
    }
    pending.world = world;
    pending.rawWorld = rawWorld;
    pending.previews = previews;
    AppController.requestPlanRender('clipboard-paste-preview');
    return previews.length > 0;
  }

function handleClipboardPastePointerDown(event, context = {}) {
    const pending = currentClipboardPastePending();
    if (!pending || context.currentView && context.currentView !== 'plan') return false;
    if (event?.button !== undefined && event.button !== 0) return false;
    if (!pending.world && !updateClipboardPastePreview(event)) return false;
    pending.gesture = {
      active: true,
      moved: false,
      startScreen: { x: Number(event?.clientX) || 0, y: Number(event?.clientY) || 0 },
      camStart: { x: Number(S.camera2D?.x) || 0, z: Number(S.camera2D?.z) || 0 },
      startScale: Number(S.camera2D?.scale) || 1
    };
    event?.preventDefault?.();
    event?.stopPropagation?.();
    setStatus('Kopie platzieren oder Plan verschieben');
    return true;
  }

function updateClipboardPastePanGesture(pending, event) {
    const gesture = pending?.gesture;
    if (!gesture?.active || !event || !(event.buttons & 1)) return false;
    const dx = (Number(event.clientX) || 0) - (Number(gesture.startScreen?.x) || 0);
    const dy = (Number(event.clientY) || 0) - (Number(gesture.startScreen?.y) || 0);
    if (!gesture.moved && Math.hypot(dx, dy) < CLIPBOARD_PASTE_PAN_TOLERANCE_PX) return false;
    gesture.moved = true;
    const scale = Number.isFinite(Number(gesture.startScale)) && Number(gesture.startScale) > 0
      ? Number(gesture.startScale)
      : Number(S.camera2D?.scale) || 1;
    S.camera2D.x = (Number(gesture.camStart?.x) || 0) - dx / scale;
    S.camera2D.z = (Number(gesture.camStart?.z) || 0) - dy / scale;
    const world = clipboardPointerWorld(event);
    if (world) {
      pending.gesture = null;
      updateClipboardPastePreview(event);
      pending.gesture = gesture;
    }
    setStatus('Plan wird verschoben');
    AppController.startPlanNavigationLod('clipboard-paste-pan');
    AppController.requestPlanRender('clipboard-paste-pan', { kind: 'navigation' });
    event.preventDefault?.();
    return true;
  }

function handleClipboardPastePointerUp(event, context = {}) {
    const pending = currentClipboardPastePending();
    const gesture = pending?.gesture;
    if (!pending || !gesture?.active) return false;
    if (event?.type !== 'blur' && context.currentView && context.currentView !== 'plan') return false;
    pending.gesture = null;
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (event?.type === 'blur' && pending.ownerContext) {clearClipboardPastePending();return true;}
    if (event?.type === 'blur') {
      setStatus('Kopie am Mauszeiger platzieren');
      return true;
    }
    if (gesture.moved) {
      updateClipboardPastePreview(event);
      setStatus('Kopie am Mauszeiger platzieren');
      return true;
    }
    if ((pending.ownerContext || !pending.world) && !updateClipboardPastePreview(event)) return true;
    return commitClipboardPastePending();
  }

function commitClipboardPastePending() {
    const pending = currentClipboardPastePending();
    if (!pending?.payload) return false;
    const expectedTotal = clipboardPayloadObjectCount(pending.payload);
    if (!expectedTotal) {
      clearClipboardPastePending();
      return false;
    }
    if(!validOwnerPaste(pending.payload))return false;
    const owned=ownerPaste(pending.payload);
    if(!owned)recordUndo(expectedTotal === 1 ? 'Objekt einfuegen' : 'Objekte einfuegen');
    const fields = allMultiSelectionFields();
    const ids = emptyMultiSelection();
    const previewBySystem = new Map((pending.previews || []).map(preview => [preview.systemId, preview]));
    for (const entry of clipboardPasteEntries(pending.payload)) {
      const preview = previewBySystem.get(entry.systemId);
      const pasteContext = {
        kind: entry.key,
        [entry.key]: entry.items,
        items: entry.items,
        target: pending.world,
        world: pending.world,
        rawWorld: pending.rawWorld
      };
      if (preview?.delta) pasteContext.delta = preview.delta;
      const result = SystemRegistry.pasteClipboardPayload(entry.systemId, pasteContext);
      if (!result) continue;
      for (const f of fields) {
        if (Array.isArray(result[f])) ids[f].push(...result[f]);
      }
    }
    const total = fields.reduce((sum, f) => sum + (ids[f]?.length || 0), 0);
    clearClipboardPastePending({ status: false });
    if (!total) {
      if(!owned)setStatus('Zwischenablage konnte nicht eingefuegt werden');
      return false;
    }
    selectPastedClipboardObjects(ids);
    AppController.refresh(false);
    setStatus(`${total} Objekt${total === 1 ? '' : 'e'} eingefuegt`);
    return true;
  }

function renderClipboardPastePreview(parentLayer) {
    const pending = currentClipboardPastePending();
    if (!pending?.previews?.length || !parentLayer) return false;
    let rendered = false;
    for (const preview of pending.previews) {
      if (!preview?.systemId) continue;
      if (SystemRegistry.renderClipboardPreview(preview.systemId, { parentLayer, preview }) === true) rendered = true;
    }
    return rendered;
  }

function clipboardRemapId(sourceId, map, prefix) {
    if (!sourceId) return uid(prefix);
    if (!map.has(sourceId)) map.set(sourceId, uid(prefix));
    return map.get(sourceId);
  }

function pasteObstaclesFromClipboard(obstacles = [], offset = CLIPBOARD_PASTE_OFFSET_MM) {
    if (!obstacles.length) return [];
    ensureObstacleState();
    const pasted = obstacles.map(obstacle => normalizeObstacle({
      ...cloneDeep(obstacle),
      id: uid('obs'),
      start: {
        x: (Number(obstacle.start?.x) || 0) + offset,
        z: (Number(obstacle.start?.z) || 0) + offset
      },
      end: {
        x: (Number(obstacle.end?.x) || 0) + offset,
        z: (Number(obstacle.end?.z) || 0) + offset
      }
    })).filter(Boolean);
    S.project.obstacles.push(...pasted);
    return pasted.map(obstacle => obstacle.id);
  }

function selectPastedClipboardObjects(ids = {}) {
    const fields = allMultiSelectionFields();
    const deduped = emptyMultiSelection();
    for (const f of fields) deduped[f] = clipboardUniqueIds(ids[f] || []);
    const total = fields.reduce((sum, f) => sum + (deduped[f]?.length || 0), 0);

    SelectionController.clearGlobalSelection();

    const orderedSystems = SystemRegistry.systemsByClipboardActivationPriority()
      .filter(s => s.multiSelectionFields?.some(f => deduped[f]?.length));

    if (total === 1) {
      if (deduped.obstacleIds.length) {
        selectObstacle(deduped.obstacleIds[0]);
        return;
      }
      for (const system of orderedSystems) {
        if (SystemRegistry.selectClipboardObjects(system.id, deduped)) return;
      }
    }

    syncLegacySelectionFromMultiSelection(deduped);

    if (orderedSystems.length) {
      SystemRegistry.activateSystem(orderedSystems[0].id);
    }
  }

export {
  handleClipboardKey,
  copyToClipboardEvent,
  pasteFromClipboardEvent,
  copyCurrentSelectionToClipboard,
  buildCurrentMixedClipboardPayload,
  clipboardUniqueIds,
  clipboardPayloadObjectCount,
  setAppClipboard,
  pasteFromAppClipboard,
  pasteFromSystemClipboard,
  currentClipboardPastePending,
  clearClipboardPastePending,
  updateClipboardPastePreview,
  handleClipboardPastePointerDown,
  handleClipboardPastePointerUp,
  renderClipboardPastePreview,
  pasteMixedObjects,
  clipboardRemapId,
  pasteObstaclesFromClipboard,
  selectPastedClipboardObjects
};
